<script setup lang="ts">
import { computed, ref, useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

import { CrossIcon, UploadIcon, XlsIcon, startViewTransition } from "@/shared/ui";

const XLSX_ACCEPT = ".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet";

const file = defineModel<File | null>({ required: true });
const { disabled = false } = defineProps<{ disabled?: boolean }>();

const { t } = useI18n({ useScope: "global" });
const input = useTemplateRef("input");

const isInvalidFormat = ref(false);

/** dragenter/dragleave fire on every child, so the counter tells when the cursor really left */
const dragDepth = ref(0);
const isDragOver = computed(() => dragDepth.value > 0);

function isXlsx(candidate: File): boolean {
    return candidate.name.toLowerCase().endsWith(".xlsx");
}

function selectFile(candidate: File | undefined): void {
    if (!candidate || disabled) return;

    void startViewTransition(() => {
        isInvalidFormat.value = !isXlsx(candidate);
        if (!isInvalidFormat.value) file.value = candidate;
    });
}

function onInputChange(event: Event): void {
    const target = event.target as HTMLInputElement;
    selectFile(target.files?.[0]);
    // Allows choosing the same file again after it was removed
    target.value = "";
}

function onDrop(event: DragEvent): void {
    dragDepth.value = 0;
    selectFile(event.dataTransfer?.files[0]);
}

function removeFile(): void {
    void startViewTransition(() => (file.value = null));
}
</script>

<!-- Выбор XLSX-файла перетаскиванием или через диалог -->
<template>
    <div class="dropzone-wrapper">
        <div
            class="dropzone"
            :class="{ dropzone_over: isDragOver, dropzone_error: isInvalidFormat }"
            @dragenter.prevent="dragDepth++"
            @dragleave.prevent="dragDepth--"
            @dragover.prevent
            @drop.prevent="onDrop"
        >
            <input
                ref="input"
                type="file"
                class="dropzone__input"
                :accept="XLSX_ACCEPT"
                :disabled="disabled"
                @change="onInputChange"
            />

            <div v-if="file" class="file">
                <XlsIcon class="file__icon" />
                <span class="file__name">{{ file.name }}</span>
                <button
                    type="button"
                    class="file__remove"
                    :aria-label="t('uploadDevicesList.removeFile')"
                    :disabled="disabled"
                    @click="removeFile"
                >
                    <CrossIcon />
                </button>
            </div>

            <template v-else>
                <UploadIcon class="dropzone__icon" />
                <p class="dropzone__text">{{ t("uploadDevicesList.dropHere") }}</p>
                <p class="dropzone__or">{{ t("uploadDevicesList.or") }}</p>
                <button
                    type="button"
                    class="dropzone__choose"
                    :disabled="disabled"
                    @click="input?.click()"
                >
                    {{ t("uploadDevicesList.chooseFile") }}
                </button>
            </template>
        </div>

        <p v-if="isInvalidFormat" class="error">{{ t("uploadDevicesList.invalidFormat") }}</p>
    </div>
</template>

<style scoped>
.dropzone {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 32px 16px;
    border: 1px dashed #d0d5dd;
    border-radius: 8px;
    background-color: #f7f8f9;
    text-align: center;
    transition:
        border-color 150ms ease,
        background-color 150ms ease;
}

.dropzone_over {
    border-color: var(--custom-color-main);
    background-color: #f4f2fc;
}

.dropzone_error {
    border-color: var(--color-error);
}

.dropzone__input {
    display: none;
}

.dropzone__icon {
    margin-bottom: 16px;
}

.dropzone__text,
.dropzone__or {
    margin: 0 0 12px;
    line-height: 1.25;
    user-select: none;
}

.dropzone__or {
    color: var(--custom-color-gray);
}

.dropzone__choose {
    padding: 0;
    border: none;
    border-bottom: 1px dotted currentColor;
    background: none;
    color: var(--color-link);
    font: inherit;
    line-height: 1.25;
    cursor: pointer;

    &:disabled {
        cursor: not-allowed;
    }
}

.file {
    display: flex;
    align-items: center;
    gap: 8px;
    max-width: 100%;
}

.file__icon {
    flex-shrink: 0;
    color: var(--custom-color-gray);
}

.file__name {
    overflow: hidden;
    font-weight: 700;
    white-space: nowrap;
    text-overflow: ellipsis;
}

.file__remove {
    display: flex;
    flex-shrink: 0;
    padding: 4px;
    border: none;
    background: none;
    color: var(--custom-color-gray);
    cursor: pointer;

    &:hover {
        color: var(--custom-color-text);
    }

    &:disabled {
        cursor: not-allowed;
    }
}

.error {
    margin: 6px 0 0;
    color: var(--color-error);
    font-size: calc(var(--custom-font-size) * 0.857);
    line-height: 1.333;
}
</style>
